import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { apiClient, axiosInstance } from '../../services/apiClient';
import { fetchSiteImageAsDataUrl } from '../../utils/siteImage';
import { SiteImageUploadDTO } from '../../generated/api-client';

import { SiteFormData } from '../../schemas/siteFormSchema';
import { TriStateCheckbox } from '../common/TriStateCheckbox';
import { InputAddress } from '../common/InputAddress';
import GoogleMap, { GoogleMapHandle } from '../common/Map';
import { MAP_SIZE } from '../../utils/googleMaps';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { SiteFramingAiDialog } from './SiteFramingAiDialog';

interface SiteDetailFormProps {
  showMap?: boolean;
  siteId?: string;
  /** ID della diagnosi corrente: necessario per abilitare l'autofill AI dell'inquadramento sito. */
  diagnosisId?: string;
  mapContainerRef?: React.RefObject<HTMLDivElement>;
  img?: string | null;
  onChangeImg?: (imagePath: string | null) => void;
  isDiagnosisSite?: boolean;
}

export const SiteDetailForm: React.FC<SiteDetailFormProps> = ({
  showMap = true,
  siteId,
  diagnosisId,
  mapContainerRef: externalMapContainerRef,
  img,
  onChangeImg,
  isDiagnosisSite = false,
}) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SiteFormData>();
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const internalMapContainerRef = React.useRef<HTMLDivElement>(null);
  const mapContainerRef = externalMapContainerRef || internalMapContainerRef;
  const googleMapRef = React.useRef<GoogleMapHandle>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [isCapturingMap, setIsCapturingMap] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [siteFramingAiOpen, setSiteFramingAiOpen] = React.useState(false);
  const siteFramingValue = watch('site_framing');

  const refreshPreviewFromApi = React.useCallback(async () => {
    if (!siteId) return;
    const dataUrl = await fetchSiteImageAsDataUrl(axiosInstance, siteId, isDiagnosisSite);
    setImagePreview(dataUrl);
  }, [siteId, isDiagnosisSite]);

  const address = watch('address');
  const allowAutoMapSyncRef = React.useRef(true);
  const siteIdRef = React.useRef(siteId);

  React.useEffect(() => {
    siteIdRef.current = siteId;
    allowAutoMapSyncRef.current = true;
  }, [siteId]);

  React.useEffect(() => {
    const trimmed = address?.trim();
    if (!trimmed || !allowAutoMapSyncRef.current) return;

    const timer = window.setTimeout(() => {
      if (siteIdRef.current !== siteId) return;
      allowAutoMapSyncRef.current = false;
      googleMapRef.current?.goToAddress(trimmed);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [siteId, address]);

  const handleAddressCommitted = React.useCallback((value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      googleMapRef.current?.goToAddress(trimmed);
    }
  }, []);

  const showProperties = watch('norm_unicei_16247_2');
  const climaticZone = watch('climatic_zone');
  const isUserZone = climaticZone === 'U';

  React.useEffect(() => {
    if (img?.startsWith('http')) {
      setImagePreview(img);
      return;
    }
    if (!siteId || !img) {
      setImagePreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const dataUrl = await fetchSiteImageAsDataUrl(axiosInstance, siteId, isDiagnosisSite);
      if (!cancelled) {
        setImagePreview(dataUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [img, siteId, isDiagnosisSite]);

  const uploadImageMutation = useMutation(
    async (imageBase64: string) => {
      if (!siteId) {
        throw new Error('ID sito mancante');
      }

      if (isDiagnosisSite) {
        return apiClient.upload_site_image_diagnosis_customer_sites__site_id__image_post(
          siteId,
          new SiteImageUploadDTO({ image_base64: imageBase64 })
        );
      } else {
        return apiClient.upload_site_image_customer_sites__site_id__image_post(
          siteId,
          new SiteImageUploadDTO({ image_base64: imageBase64 })
        );
      }
    },
    {
      onSuccess: async updatedSite => {
        toast.success('Immagine caricata con successo');
        const imagePath = (updatedSite as any).img || null;
        if (onChangeImg) {
          onChangeImg(imagePath);
        }
        await refreshPreviewFromApi();
        const queryKey = isDiagnosisSite
          ? ['diagnosis-customer-sites', siteId]
          : ['customer-sites', siteId];
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore durante il caricamento dell'immagine");
      },
    }
  );

  const deleteImageMutation = useMutation(
    async () => {
      if (!siteId) {
        throw new Error('ID sito mancante');
      }

      if (isDiagnosisSite) {
        return apiClient.delete_site_image_diagnosis_customer_sites__site_id__image_delete(siteId);
      } else {
        return apiClient.delete_site_image_customer_sites__site_id__image_delete(siteId);
      }
    },
    {
      onSuccess: () => {
        toast.success('Immagine eliminata con successo');
        setImagePreview(null);
        if (onChangeImg) {
          onChangeImg(null);
        }
        const queryKey = isDiagnosisSite
          ? ['diagnosis-customer-sites', siteId]
          : ['customer-sites', siteId];
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore durante la cancellazione dell'immagine");
      },
    }
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verifica che sia un'immagine
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    // Verifica la dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'immagine deve essere inferiore a 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setImagePreview(base64String);
      if (siteId) {
        uploadImageMutation.mutate(base64String);
      }
    };
    reader.onerror = () => {
      toast.error('Errore durante la lettura del file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteImage = () => {
    // Se c'è un siteId, chiama l'endpoint per cancellare l'immagine
    if (siteId) {
      deleteImageMutation.mutate(undefined, {
        onSettled: () => {
          setShowDeleteConfirm(false);
        },
      });
    } else {
      // Altrimenti rimuovi solo localmente
      setImagePreview(null);
      if (onChangeImg) {
        onChangeImg(null);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDeleteImage = () => {
    setShowDeleteConfirm(false);
  };

  const handleCaptureMap = async () => {
    if (!siteId) {
      toast.error('Salva il sito prima di catturare la mappa');
      return;
    }

    if (!googleMapRef.current) {
      toast.error('Mappa non pronta');
      return;
    }

    setIsCapturingMap(true);
    try {
      const dataUrl = await googleMapRef.current.captureAsDataUrl();
      setImagePreview(dataUrl);
      uploadImageMutation.mutate(dataUrl);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Errore durante la cattura della mappa';
      toast.error(message);
    } finally {
      setIsCapturingMap(false);
    }
  };

  return (
    <>
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Dati principali
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 3 }}>
                  <InputAddress
                    name="address"
                    label="Indirizzo sito"
                    required
                    onAddressCommitted={handleAddressCommitted}
                  />
                </Box>
                <TextField
                  {...register('covered_area', { valueAsNumber: true })}
                  fullWidth
                  type="number"
                  label="Superficie coperta totale [m²]"
                  error={!!errors.covered_area}
                  helperText={errors.covered_area?.message as string}
                  variant="outlined"
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ mb: 2 }}
                />
                <Controller
                  name="iso_50001_certified_site"
                  control={control}
                  render={({ field }) => (
                    <TriStateCheckbox
                      label="Sito certificato ISO 50001"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={!!errors.iso_50001_certified_site}
                      helperText={errors.iso_50001_certified_site?.message as string}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="telecom_network_presence"
                  control={control}
                  render={({ field }) => (
                    <TriStateCheckbox
                      label="Presenza rete di teleriscaldamento entro 1 km dallo stabilimento"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={!!errors.telecom_network_presence}
                      helperText={errors.telecom_network_presence?.message as string}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="cogeneration_availability"
                  control={control}
                  render={({ field }) => (
                    <TriStateCheckbox
                      label="Disponibilità di calore da cogenerazione entro 1 km dallo stabilimento"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={!!errors.cogeneration_availability}
                      helperText={errors.cogeneration_availability?.message as string}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>
              {showMap && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box ref={mapContainerRef} sx={{ width: MAP_SIZE, height: MAP_SIZE, position: 'relative' }}>
                    <GoogleMap
                      ref={googleMapRef}
                      address={address}
                      resetKey={siteId ?? '__new__'}
                    />
                  </Box>

                  {/* Campo per caricare immagine */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Immagine del sito
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {imagePreview && (
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            maxHeight: 300,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            bgcolor: 'grey.100',
                          }}
                        >
                          <img
                            src={imagePreview}
                            alt="Preview sito"
                            style={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              objectFit: 'contain',
                            }}
                          />
                          <IconButton
                            onClick={handleRemoveImage}
                            disabled={deleteImageMutation.isLoading}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'error.dark',
                              },
                              '&:disabled': {
                                bgcolor: 'action.disabledBackground',
                                color: 'action.disabled',
                              },
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<PhotoCameraIcon />}
                          disabled={uploadImageMutation.isLoading || !siteId}
                        >
                          {imagePreview ? 'Cambia immagine' : 'Carica immagine'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                          />
                        </Button>
                        {uploadImageMutation.isLoading && (
                          <Typography variant="body2" color="text.secondary">
                            Caricamento...
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          onClick={handleCaptureMap}
                          disabled={
                            !address?.trim() || isCapturingMap || uploadImageMutation.isLoading || !siteId
                          }
                          startIcon={<MapIcon />}
                        >
                          {isCapturingMap ? 'Cattura in corso...' : 'Salva immagine mappa'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {isDiagnosisSite && (
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Inquadramento sito
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Breve paragrafo (massimo mezza pagina) che descrive cosa fa l'azienda in questo
                sito/stabilimento: puoi scriverlo manualmente oppure generarlo con l'AI, tramite
                ricerca online o a partire da documenti caricati.
              </Typography>
              <TextField
                {...register('site_framing')}
                fullWidth
                label="Inquadramento del sito"
                placeholder="Descrivi cosa produce o quali attività svolge l'azienda in questo sito..."
                error={!!errors.site_framing}
                helperText={
                  errors.site_framing?.message ||
                  `${(siteFramingValue ?? '').length} / 3000 caratteri`
                }
                variant="outlined"
                // Il campo è non controllato (register): senza shrink forzato la label
                // non si alza quando il testo viene impostato da setValue (AI autofill).
                InputLabelProps={{ shrink: true }}
                multiline
                minRows={6}
                maxRows={14}
              />
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<PsychologyIcon />}
                  onClick={() => setSiteFramingAiOpen(true)}
                  disabled={!diagnosisId}
                >
                  Chiedi all'AI
                </Button>
                {!diagnosisId && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                    Salva prima i dati della diagnosi per usare l'autofill AI.
                  </Typography>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Confini della diagnosi energetica
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <TextField
              {...register('excluded_areas')}
              fullWidth
              label="Eventuali aree escluse dalla diagnosi energetica"
              error={!!errors.excluded_areas}
              helperText={errors.excluded_areas?.message}
              variant="outlined"
              multiline
              rows={3}
            />

            <Typography variant="subtitle1" fontWeight={600}>
              Norme tecniche applicate
            </Typography>

            <Controller
              name="norm_unicei_16247_1"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value ?? true} disabled />}
                  label='UNI CEI 16247-1 "Diagnosi energetiche - Parte 1: Requisiti generali" (sempre applicata)'
                />
              )}
            />

            <Controller
              name="norm_unicei_16247_2"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value ?? false} />}
                  label='UNI CEI 16247-2 "Diagnosi energetiche - Parte 2: Edifici"'
                />
              )}
            />

            <Controller
              name="norm_unicei_16247_3"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value ?? false} />}
                  label='UNI CEI 16247-3 "Diagnosi energetiche - Parte 3: Processi"'
                />
              )}
            />

            <Controller
              name="norm_unicei_16247_4"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value ?? false} />}
                  label='UNI CEI 16247-4 "Diagnosi energetiche - Parte 4: Trasporto"'
                />
              )}
            />

            <TextField
              {...register('notes')}
              fullWidth
              label="Note e commenti"
              error={!!errors.notes}
              helperText={errors.notes?.message}
              variant="outlined"
              multiline
              rows={3}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Dati climatici
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth error={!!errors.climatic_zone}>
                  <InputLabel>Zona climatica</InputLabel>
                  <Controller
                    name="climatic_zone"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Zona climatica"
                        value={field.value || ''}
                        onChange={e => field.onChange(e.target.value || undefined)}
                      >
                        <MenuItem value="A">A</MenuItem>
                        <MenuItem value="B">B</MenuItem>
                        <MenuItem value="C">C</MenuItem>
                        <MenuItem value="D">D</MenuItem>
                        <MenuItem value="E">E</MenuItem>
                        <MenuItem value="U">Utente</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.climatic_zone && (
                    <FormHelperText>{errors.climatic_zone.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  {...register('start_summer')}
                  fullWidth
                  label="Inizio estate"
                  error={!!errors.start_summer}
                  helperText={
                    errors.start_summer?.message ||
                    (isUserZone ? 'Formato: DD/MM' : 'Calcolato automaticamente')
                  }
                  variant="outlined"
                  disabled={!isUserZone && !!climaticZone}
                  placeholder="DD/MM"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  {...register('start_winter')}
                  fullWidth
                  label="Inizio inverno"
                  error={!!errors.start_winter}
                  helperText={
                    errors.start_winter?.message ||
                    (isUserZone ? 'Formato: DD/MM' : 'Calcolato automaticamente')
                  }
                  variant="outlined"
                  disabled={!isUserZone && !!climaticZone}
                  placeholder="DD/MM"
                />
              </Grid>
            </Grid>

            {climaticZone && !isUserZone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Le date vengono calcolate automaticamente in base alla zona climatica selezionata.
              </Typography>
            )}

            {isUserZone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Formato date: DD/MM (es. 15/03 per il 15 marzo)
              </Typography>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Flag contatori
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="POD e.e. in prelievo (PODp)"
                  type="number"
                  inputProps={{ min: 0 }}
                  {...register('has_podp', { valueAsNumber: true })}
                  error={!!errors.has_podp}
                  helperText={errors.has_podp?.message}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="POD e.e. in immissione (PODi)"
                  type="number"
                  inputProps={{ min: 0 }}
                  {...register('has_podi', { valueAsNumber: true })}
                  error={!!errors.has_podi}
                  helperText={errors.has_podi?.message}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contatori di produzione di e.e. (PROD)"
                  type="number"
                  inputProps={{ min: 0 }}
                  {...register('has_prod', { valueAsNumber: true })}
                  error={!!errors.has_prod}
                  helperText={errors.has_prod?.message}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="PDR gas (PDR)"
                  type="number"
                  inputProps={{ min: 0 }}
                  {...register('has_pdr', { valueAsNumber: true })}
                  error={!!errors.has_pdr}
                  helperText={errors.has_pdr?.message}
                />
              </Grid>
            </Grid>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {showProperties && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Scheda immobile
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="property"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      freeSolo
                      options={['Proprietà', 'Affitto']}
                      value={field.value ?? ''}
                      onInputChange={(_, value) => field.onChange(value ?? '')}
                      onChange={(_, value) =>
                        field.onChange(typeof value === 'string' ? value : (value ?? ''))
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Immobile"
                          error={!!errors.property}
                          helperText={errors.property?.message}
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  {...register('type')}
                  fullWidth
                  label="Tipologia"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="availability_until"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Disponibilità fino al"
                      value={field.value ? dayjs(field.value as Date) : null}
                      onChange={value => {
                        field.onChange(value ? value.toDate() : null);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.availability_until,
                          helperText: errors.availability_until?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="construction_year"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Anno di costruzione"
                      value={field.value ? dayjs(field.value as Date) : null}
                      onChange={value => {
                        // Solo anno: il valore memorizzato resta una Date (1° gennaio dell'anno
                        // scelto), così lo schema/API non cambiano; cambia solo l'editor.
                        field.onChange(value ? value.startOf('year').toDate() : null);
                      }}
                      views={['year']}
                      format="YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.construction_year,
                          helperText: errors.construction_year?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="renovation_year"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Anno di ristrutturazione"
                      value={field.value ? dayjs(field.value as Date) : null}
                      onChange={value => {
                        // Solo anno: il valore memorizzato resta una Date (1° gennaio dell'anno
                        // scelto), così lo schema/API non cambiano; cambia solo l'editor.
                        field.onChange(value ? value.startOf('year').toDate() : null);
                      }}
                      views={['year']}
                      format="YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.renovation_year,
                          helperText: errors.renovation_year?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Controller
                      name="energy_performance_certificate"
                      control={control}
                      render={({ field }) => <Checkbox {...field} checked={field.value ?? false} />}
                    />
                  }
                  label="Attestato di prestazione energetica presente"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  {...register('efficiency_class')}
                  fullWidth
                  label="Classe di efficienza"
                  required={!!showProperties}
                  error={!!errors.efficiency_class}
                  helperText={errors.efficiency_class?.message}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Caratteristiche involucro
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    {...register('building_envelope_walls_floors')}
                    fullWidth
                    label="Pareti e solai"
                    error={!!errors.building_envelope_walls_floors}
                    helperText={errors.building_envelope_walls_floors?.message}
                    variant="outlined"
                    multiline
                  />
                  <TextField
                    {...register('building_envelope_insulation')}
                    fullWidth
                    label="Coibentazione"
                    error={!!errors.building_envelope_insulation}
                    helperText={errors.building_envelope_insulation?.message}
                    variant="outlined"
                    multiline
                  />
                  <TextField
                    {...register('building_envelope_transmittance')}
                    fullWidth
                    label="Trasmittanza"
                    error={!!errors.building_envelope_transmittance}
                    helperText={errors.building_envelope_transmittance?.message}
                    variant="outlined"
                    multiline
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Caratteristiche infissi
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    {...register('fixtures_glass')}
                    fullWidth
                    label="Vetro"
                    error={!!errors.fixtures_glass}
                    helperText={errors.fixtures_glass?.message}
                    variant="outlined"
                    multiline
                  />
                  <TextField
                    {...register('fixtures_frame')}
                    fullWidth
                    label="Telaio"
                    error={!!errors.fixtures_frame}
                    helperText={errors.fixtures_frame?.message}
                    variant="outlined"
                    multiline
                  />
                  <TextField
                    {...register('fixtures_transmittance')}
                    fullWidth
                    label="Trasmittanza"
                    error={!!errors.fixtures_transmittance}
                    helperText={errors.fixtures_transmittance?.message}
                    variant="outlined"
                    multiline
                  />
                  <TextField
                    {...register('fixtures_shading')}
                    fullWidth
                    label="Eventuali schermature"
                    error={!!errors.fixtures_shading}
                    helperText={errors.fixtures_shading?.message}
                    variant="outlined"
                    multiline
                  />
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Conferma eliminazione"
        content="Sei sicuro di voler eliminare l'immagine del sito?"
        confirmText="Elimina"
        cancelText="Annulla"
        loading={deleteImageMutation.isLoading}
        onCancel={handleCancelDeleteImage}
        onConfirm={handleConfirmDeleteImage}
      />

      {isDiagnosisSite && (
        <SiteFramingAiDialog
          open={siteFramingAiOpen}
          diagnosisId={diagnosisId}
          currentText={siteFramingValue ?? ''}
          onClose={() => setSiteFramingAiOpen(false)}
          onGenerated={text =>
            setValue('site_framing', text, { shouldDirty: true, shouldValidate: true })
          }
        />
      )}
    </>
  );
};
